import { CatalogVariantFragmentFragment } from '@notino/shared/definitions/types';

import { IPrice } from '@containers/ProductListing/model';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';

import {
  useGetBetterOfferText,
  useGetOriginalPriceTooltip,
  useGetYouSaveText,
  useRecentPriceTooltip,
} from './utils';

// https://gitlab.notino.com/web/nushopweb/-/blob/master/docs/diagrams/price-logic.md
interface IReturnValue {
  discountText?: string;
  topPrice?: {
    value: IPrice;
    isCrossed: boolean;
    tooltip: JSX.Element;
  };
  bottomRecentPrice?: { value: IPrice; tooltip: JSX.Element };

  /** Indicates whether converted/recommended/maxHistorical price is shown. */
  rrpShown: boolean;
  minimalPriceShown: boolean;
}

export type UsePriceLogicParameters = Pick<
  CatalogVariantFragmentFragment,
  'attributes' | 'price' | 'originalPrice' | 'recentMinPrice'
>;

export const usePriceLogic = ({
  attributes,
  price,
  originalPrice,
  recentMinPrice,
}: UsePriceLogicParameters): IReturnValue => {
  const getYouSaveText = useGetYouSaveText();
  const getBetterOfferText = useGetBetterOfferText();
  const getOriginalPriceTooltip = useGetOriginalPriceTooltip();
  const { strictSalesDisplay } = useFeatureFlags();
  const recentPriceTooltip = useRecentPriceTooltip();

  const isActionOrClearance: boolean =
    attributes?.Action || attributes?.Clearance;

  const hasVoucher = !!attributes.VoucherDiscount;

  if (isActionOrClearance) {
    if (recentMinPrice?.isActionEligible) {
      return {
        minimalPriceShown: true,
        rrpShown: false,
        discountText: getYouSaveText(price.value, recentMinPrice.value),
        topPrice: {
          value: recentMinPrice,
          isCrossed: true,
          tooltip: recentPriceTooltip,
        },
      };
    }

    if (recentMinPrice) {
      return originalPrice?.value
        ? {
            rrpShown: false,
            minimalPriceShown: true,
            bottomRecentPrice: {
              value: recentMinPrice,
              tooltip: recentPriceTooltip,
            },
          }
        : {
            minimalPriceShown: true,
            rrpShown: false,
            bottomRecentPrice: {
              value: recentMinPrice,
              tooltip: recentPriceTooltip,
            },
          };
    }

    return originalPrice?.value
      ? {
          rrpShown: false,
          minimalPriceShown: false,
        }
      : {
          rrpShown: false,
          minimalPriceShown: false,
        };
  }

  if (hasVoucher && strictSalesDisplay) {
    return recentMinPrice
      ? {
          minimalPriceShown: true,
          rrpShown: false,
          bottomRecentPrice: {
            value: recentMinPrice,
            tooltip: recentPriceTooltip,
          },
        }
      : { minimalPriceShown: false, rrpShown: false };
  }

  return originalPrice?.value
    ? {
        rrpShown: true,
        minimalPriceShown: false,
        topPrice: {
          value: originalPrice,
          isCrossed: false,
          tooltip: getOriginalPriceTooltip(originalPrice.type),
        },
        discountText: getBetterOfferText(price.value, originalPrice.value),
      }
    : { minimalPriceShown: false, rrpShown: false };
};
